import { useState, useEffect, useMemo } from 'react';
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community';
import { ChartProps } from '../Interfaces/Charts';
import VCenteredModal from '../Components/VCenteredModal';
import { DESCRIPTION_MESSAGE, DOWNLOAD_MESSAGE, POPOUT_MESSAGE } from '../Constants';

ModuleRegistry.registerModules([AllCommunityModule]);

export default function LMPContourMap(props: ChartProps) {
	// ***************************************************************************************************************************************************
	// *** Custom Block: the following two defintions are custom to each table this code can produce,
	//      they provide the location for the data and the names of the columns to display
	const api_url = `${process.env.REACT_APP_DATABROKER_URL}/MISORTWD/lmpcontourmap.html`;
	const interval_url = `${process.env.REACT_APP_DATABROKER_URL}/MISORTWDDataBroker/DataBrokerServices.asmx?messageType=GetContourImageryLatestTimestamp`;
	const popout_path = `${process.env.REACT_APP_DATABROKER_URL}/MISORTWD/lmpcontourmap.html`;
	const modalHeader: React.ReactNode = <span>LMP Contour Map</span>;

	const descriptionBody: React.ReactNode = useMemo((): React.ReactNode => {
		return (
			<>
				<span>
					The LMP Contour Map displays the current Locational Marginal Prices (LMP) across the MISO footprint. The map will automatically refresh every 5 minutes to show
					updated information. Due to the nature of UDS approval, it is possible for the interval between 5 minute periods to vary widely. Under certain market
					circumstances the map display below may not display each 5 minute period.
				</span>
				<br />
			</>
		);
	}, []);
	// ***************************************************************************************************************************************************

	const [buttonClicked] = [props.buttonClicked];
	const [showModal, setShowModal] = useState(false);
	const [modalBody, setModalBody] = useState<React.ReactNode>(<></>);

	useEffect(() => {
		switch (buttonClicked) {
			case DESCRIPTION_MESSAGE:
				setModalBody(descriptionBody);
				setShowModal(true);
				break;
			case DOWNLOAD_MESSAGE:
				setShowModal(true);
				break;
			case POPOUT_MESSAGE:
				const newWinddow = window.open(popout_path, '_blank', 'width=1920,height=1080,noopener,noreferrer');
				if (newWinddow) newWinddow.opener = null;
				break;
			default:
				break;
		}
	}, [buttonClicked, api_url, descriptionBody, popout_path]);

	const [iframeLoading, setIframeLoading] = useState(true);
	const [iframeError, setIframeError] = useState<string | null>(null);
	const [intervalText, setIntervalText] = useState<string>('');

	useEffect(() => {
		fetch(interval_url)
			.then((response) => response.text())
			.then((text) => {
				let formatted = '';
				try {
					const obj = JSON.parse(text);
					if (obj.year && obj.month && obj.day && obj.hour !== undefined && obj.minute !== undefined) {
						const monthNames = [
							'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
							'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
						];
						const monthAbbr = monthNames[obj.month - 1] || obj.month.toString().padStart(2, '0');
						formatted = `${obj.day.toString().padStart(2, '0')}-${monthAbbr}-${obj.year} - Interval ${obj.hour.toString().padStart(2, '0')}:${obj.minute.toString().padStart(2, '0')} EST`;
					} else {
						formatted = text;
					}
				} catch {
					formatted = text;
				}
				setIntervalText(formatted);
			})
			.catch(() => setIntervalText(''));
	}, [interval_url]);

	return (
		<>
			<VCenteredModal show={showModal} onHide={() => setShowModal(false)} headercontent={modalHeader} bodycontent={modalBody} />
			<div className='lmp-contour-map-container' style={{ width: '100%', height: '800px' }}>
				<div className='interval-line-less-padding'>{intervalText}</div>
				{iframeLoading && !iframeError && <div className='loading-and-error-box'>Loading...</div>}
				{iframeError && <div className='loading-and-error-box'>{iframeError}</div>}
				<iframe
					src={api_url}
					title='LMP Contour Map'
					style={{ width: '100%', height: '750px', border: 'none', display: iframeLoading || iframeError ? 'none' : 'block' }}
					onLoad={() => setIframeLoading(false)}
					onError={() => {
						setIframeLoading(false);
						setIframeError('Failed to load the LMP Contour Map.');
					}}
				/>
			</div>
		</>
	);
}
